import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  ArrowLeft,
  Clock,
  CreditCard,
  Building,
  Flag,
  Briefcase,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { mockApplicants } from '@mocks/applicants';
import { mockApplications } from '@mocks/licenses';
import { mockAttachments } from '@mocks/attachments';
import { mockAppointments } from '@mocks/appointments';
import { LICENSE_CATEGORY_LABELS } from '@utils/constants';

export const ApplicantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'applications' | 'documents' | 'appointments'>('overview');

  // Get applicant data
  const applicant = mockApplicants.find((app) => app.id === parseInt(id || '0', 10));

  // Get related data
  const applications = mockApplications.filter((app) => app.applicant_id === parseInt(id || '0', 10));
  const documents = mockAttachments.filter((doc) => doc.applicant_id === parseInt(id || '0', 10));
  const appointments = mockAppointments.filter((apt) => apt.applicant_id === parseInt(id || '0', 10));

  if (!applicant) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-16">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Applicant Not Found</h3>
            <p className="text-gray-600 mb-4">The applicant you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/admin/applicants')} icon={<ArrowLeft />}>
              Back to Applicants
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    if (!status) {
      return <Badge variant="default">Unknown</Badge>;
    }

    switch (status) {
      case 'APPROVED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />}>Rejected</Badge>;
      case 'DOCUMENT_REVIEW':
        return <Badge variant="warning" icon={<Clock className="h-3 w-3" />}>Document Review</Badge>;
      case 'SCHEDULED':
        return <Badge variant="info" icon={<Calendar className="h-3 w-3" />}>Scheduled</Badge>;
      case 'COMPLETED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Completed</Badge>;
      case 'CANCELLED':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />}>Cancelled</Badge>;
      case 'NO_SHOW':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />}>No Show</Badge>;
      case 'VERIFIED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Verified</Badge>;
      case 'PENDING':
        return <Badge variant="warning" icon={<Clock className="h-3 w-3" />}>Pending</Badge>;
      default:
        return <Badge variant="default">{status.replace('_', ' ')}</Badge>;
    }
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: <User className="h-4 w-4" /> },
    { id: 'applications' as const, label: 'Applications', icon: <FileText className="h-4 w-4" />, count: applications.length },
    { id: 'documents' as const, label: 'Documents', icon: <FileText className="h-4 w-4" />, count: documents.length },
    { id: 'appointments' as const, label: 'Appointments', icon: <Calendar className="h-4 w-4" />, count: appointments.length },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/admin/applicants')}
          className="mb-4"
        >
          Back to Applicants
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark">
              {applicant.first_name} {applicant.middle_name} {applicant.last_name}
            </h1>
            <p className="text-ncaa-medium mt-1">Applicant ID: #{applicant.id}</p>
          </div>
          <Badge variant={applicant.nin_status === 'VERIFIED' ? 'success' : 'warning'} size="lg">
            {applicant.nin_status}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-ncaa-primary text-ncaa-primary'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className="ml-1 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Personal Information" icon={<User className="h-5 w-5" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Full Name</p>
                  <p className="font-medium text-gray-900">
                    {applicant.first_name} {applicant.middle_name} {applicant.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date of Birth</p>
                  <p className="font-medium text-gray-900">{formatDate(applicant.dob)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Gender</p>
                  <p className="font-medium text-gray-900">{applicant.sex === 'M' ? 'Male' : applicant.sex === 'F' ? 'Female' : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Nationality</p>
                  <p className="font-medium text-gray-900">{applicant.nationality}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">NIN</p>
                  <p className="font-medium font-mono text-gray-900">{applicant.nin}</p>
                </div>
              </div>
            </Card>

            <Card title="Contact Information" icon={<Mail className="h-5 w-5" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{applicant.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{applicant.phone}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Address" icon={<MapPin className="h-5 w-5" />}>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Street Address</p>
                  <p className="font-medium text-gray-900">{applicant.address_line1 || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">City</p>
                    <p className="font-medium text-gray-900">{applicant.city || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">State</p>
                    <p className="font-medium text-gray-900">{applicant.state || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar - Quick Stats & Actions */}
          <div className="space-y-6">
            <Card title="Quick Stats">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">Applications</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600">{applications.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-900">Documents</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{documents.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">Appointments</span>
                  </div>
                  <span className="text-lg font-bold text-purple-600">{appointments.length}</span>
                </div>
              </div>
            </Card>

            <Card title="Account Information">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Registration Date</p>
                  <p className="font-medium text-gray-900">{formatDate(applicant.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                  <p className="font-medium text-gray-900">{formatDate(applicant.updated_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Verification Status</p>
                  {getStatusBadge(applicant.verification_status)}
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="space-y-4">
          {applications.length === 0 ? (
            <Card>
              <div className="text-center py-16">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Applications</h3>
                <p className="text-gray-600">This applicant hasn't submitted any applications yet.</p>
              </div>
            </Card>
          ) : (
            applications.map((app) => (
              <Card key={app.id}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {LICENSE_CATEGORY_LABELS[app.license_category]}
                      </h3>
                      <Badge variant={app.application_type === 'NEW' ? 'info' : 'warning'}>
                        {app.application_type === 'NEW' ? 'New License' : 'Renewal'}
                      </Badge>
                      {getStatusBadge(app.status)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Application ID</p>
                        <p className="font-medium text-gray-900">#{app.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Application Type</p>
                        <p className="font-medium text-gray-900">
                          {app.application_type === 'NEW' ? 'New License' : 'License Renewal'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Submitted</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(app.submitted_at || app.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Progress</p>
                        <p className="font-medium text-gray-900">
                          Step {app.current_step} of {app.total_steps}
                        </p>
                      </div>
                    </div>
                    {app.application_type === 'RENEWAL' && app.existing_license_number && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Existing License Number</p>
                        <p className="text-sm font-mono font-medium text-gray-900">{app.existing_license_number}</p>
                      </div>
                    )}
                    {app.reviewer_notes && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-medium text-blue-900 mb-1">Reviewer Notes</p>
                        <p className="text-sm text-blue-800">{app.reviewer_notes}</p>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Eye className="h-4 w-4" />}
                    onClick={() => navigate('/admin/enrolments')}
                  >
                    Review
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          {documents.length === 0 ? (
            <Card>
              <div className="text-center py-16">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents</h3>
                <p className="text-gray-600">This applicant hasn't uploaded any documents yet.</p>
              </div>
            </Card>
          ) : (
            documents.map((doc) => (
              <Card key={doc.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{doc.document_type}</h3>
                      <p className="text-sm text-gray-600">{doc.file_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">{doc.file_size}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">Uploaded {formatDate(doc.uploaded_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(doc.status)}
                    <Button size="sm" variant="outline" icon={<Eye className="h-3 w-3" />}>
                      View
                    </Button>
                    <Button size="sm" variant="outline" icon={<Download className="h-3 w-3" />}>
                      Download
                    </Button>
                  </div>
                </div>
                {doc.notes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-1">Notes</p>
                    <p className="text-sm text-gray-700">{doc.notes}</p>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="space-y-4">
          {appointments.length === 0 ? (
            <Card>
              <div className="text-center py-16">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Appointments</h3>
                <p className="text-gray-600">This applicant hasn't booked any appointments yet.</p>
              </div>
            </Card>
          ) : (
            appointments.map((apt) => (
              <Card key={apt.id}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">{apt.test_type}</h3>
                      {getStatusBadge(apt.status)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Date</p>
                        <p className="font-medium text-gray-900">{formatDate(apt.appointment_date)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Time</p>
                        <p className="font-medium text-gray-900">{apt.time_slot}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Location</p>
                        <p className="font-medium text-gray-900">{apt.center_name}</p>
                      </div>
                    </div>
                    {apt.notes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{apt.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};
